import { createFileRoute, useNavigate } from '@tanstack/react-router';
import type { LucideIcon } from 'lucide-react';
import {
  ArrowLeft,
  Calendar,
  Mail,
  MapPin,
  Pencil,
  Building2,
  Hash,
  Trash2,
  UserCheck,
  UserPlus,
  UserX,
} from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  useDeleteEmployee,
  useEmployee,
  useToggleEmployeeStatus,
  useUpdateConsent,
} from '@/features/employees/api/use-employees';
import { useEmployeeAccess } from '@/features/employees/hooks/use-employee-access';
import { CONSENT_STATUS_LABELS } from '@/features/employees/types';
import { useInviteMember } from '@/features/team/api/use-invite-member';
import { useRoles } from '@/hooks/use-roles';
import i18n from '@/lib/i18n';

export const Route = createFileRoute('/_authenticated/employees/$employeeId/')({
  component: EmployeeProfilePage,
});

function EmployeeProfilePage() {
  const { employeeId } = Route.useParams();
  const navigate = useNavigate();
  const { t } = useTranslation('common');
  const { canManageEmployees, isLoading: accessLoading, orgId } = useEmployeeAccess();
  const { data: employee, isLoading } = useEmployee(employeeId);
  const consentMutation = useUpdateConsent();
  const deleteMutation = useDeleteEmployee();
  const toggleStatusMutation = useToggleEmployeeStatus();

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showInviteDialog, setShowInviteDialog] = useState(false);

  const handleConsentToggle = async () => {
    if (!employee) return;
    const newStatus = employee.consentStatus === 'granted' ? 'revoked' : 'granted';
    try {
      await consentMutation.mutateAsync({ employeeId, consentStatus: newStatus });
      toast.success(i18n.t('settings:toast.consent', { newStatus: newStatus }));
    } catch {
      toast.error(i18n.t('settings:toast.failedToUpdateConsent'));
    }
  };

  const handleDelete = async () => {
    try {
      await deleteMutation.mutateAsync(employeeId);
      toast.success(i18n.t('settings:toast.employeeDeleted'));
      void navigate({ to: '/employees' });
    } catch {
      toast.error(i18n.t('settings:toast.failedToDeleteEmployee'));
    }
  };

  const handleToggleStatus = async () => {
    if (!employee) return;
    try {
      await toggleStatusMutation.mutateAsync({
        employeeId,
        isActive: !employee.isActive,
      });
      toast.success(
        i18n.t('settings:toast.employee', {
          status: employee.isActive
            ? i18n.t('settings:toast.statusDeactivated')
            : i18n.t('settings:toast.statusActivated'),
        })
      );
    } catch {
      toast.error(i18n.t('settings:toast.failedToUpdateStatus'));
    }
  };

  if (accessLoading || isLoading) {
    return (
      <div className="space-y-4 p-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!canManageEmployees) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-12 text-center">
          <h2 className="text-lg font-semibold">{t('accessDenied.title')}</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            {t('accessDenied.employeeRecordsDenied')}
          </p>
          <Button
            variant="outline"
            onClick={() => void navigate({ to: '/dashboard' })}
            className="mt-4"
          >
            {t('accessDenied.goToDashboard')}
          </Button>
        </div>
      </div>
    );
  }

  if (!employee) {
    return <div className="p-6 text-muted-foreground">{t('employees.employeeNotFound')}</div>;
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => void navigate({ to: '/employees' })}
            data-testid="back-button"
          >
            <ArrowLeft className="mr-1 h-4 w-4" /> {t('employees.back')}
          </Button>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold tracking-tight">{employee.name}</h1>
            <Badge variant={employee.isActive ? 'default' : 'secondary'}>
              {employee.isActive ? t('employees.active') : t('employees.inactive')}
            </Badge>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => { setShowInviteDialog(true); }}
            data-testid="invite-to-platform-button"
          >
            <UserPlus className="mr-2 h-4 w-4" />
            {t('employees.inviteToPlatform')}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() =>
              void navigate({
                to: '/employees/$employeeId/edit',
                params: { employeeId },
              })
            }
            data-testid="edit-employee-button"
          >
            <Pencil className="mr-2 h-4 w-4" />
            {t('employees.edit')}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => void handleToggleStatus()}
            disabled={toggleStatusMutation.isPending}
          >
            {employee.isActive ? (
              <>
                <UserX className="mr-2 h-4 w-4" />
                {t('employees.deactivate')}
              </>
            ) : (
              <>
                <UserCheck className="mr-2 h-4 w-4" />
                {t('employees.activate')}
              </>
            )}
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="text-destructive hover:bg-destructive/10"
            onClick={() => { setShowDeleteDialog(true); }}
            data-testid="delete-employee-button"
          >
            <Trash2 className="mr-2 h-4 w-4" />
            {t('employees.delete')}
          </Button>
        </div>
      </div>

      {/* Content Grid */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Details Card */}
        <Card>
          <CardHeader>
            <CardTitle>{t('employees.employeeDetails')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <DetailRow icon={Mail} label={t('employees.email')} value={employee.email} testId="employee-email" />
            <DetailRow
              icon={Building2}
              label={t('employees.department')}
              value={employee.departmentName ?? t('employees.notAssigned')}
            />
            <DetailRow
              icon={MapPin}
              label={t('employees.facility')}
              value={employee.facilityName ?? t('employees.notAssigned')}
            />
            <DetailRow
              icon={Hash}
              label={t('employees.externalId')}
              value={employee.employeeIdExternal ?? t('employees.notSet')}
            />
            <DetailRow
              icon={Calendar}
              label={t('employees.added')}
              value={new Date(employee.createdAt).toLocaleDateString()}
            />
            <DetailRow
              icon={Calendar}
              label={t('employees.lastUpdated')}
              value={new Date(employee.updatedAt).toLocaleDateString()}
            />
          </CardContent>
        </Card>

        {/* Consent & Surveys Card */}
        <Card>
          <CardHeader>
            <CardTitle>{t('employees.consentAndSurveys')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">{t('employees.consentStatus')}</p>
                <Badge
                  variant={
                    employee.consentStatus === 'granted'
                      ? 'default'
                      : employee.consentStatus === 'revoked'
                        ? 'destructive'
                        : 'outline'
                  }
                  className="mt-1"
                  data-testid="consent-badge"
                >
                  {CONSENT_STATUS_LABELS[employee.consentStatus]}
                </Badge>
                {employee.consentGrantedAt && (
                  <p className="mt-1 text-xs text-muted-foreground">
                    {t('employees.consentGrantedDate', { date: new Date(employee.consentGrantedAt).toLocaleDateString() })}
                  </p>
                )}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => void handleConsentToggle()}
                disabled={consentMutation.isPending}
                data-testid="toggle-consent"
              >
                {employee.consentStatus === 'granted' ? t('employees.revokeConsent') : t('employees.grantConsent')}
              </Button>
            </div>

            <div>
              <p className="text-sm font-medium text-muted-foreground">{t('employees.surveyParticipation')}</p>
              <p className="mt-1 text-3xl font-bold">{employee.surveyCount}</p>
              <p className="text-xs text-muted-foreground">{t('employees.surveysCompleted')}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Company Access */}
      {employee.organizationAccess.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>{t('employees.companyAccess')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {employee.organizationAccess.map((org) => (
                <Badge key={org.id} variant="secondary">
                  {org.name}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('employees.deleteEmployee')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('employees.deleteEmployeeConfirm', { name: employee.name })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('employees.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => void handleDelete()}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t('employees.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Invite to Platform Dialog */}
      {orgId && (
        <InviteToPlatformDialog
          orgId={orgId}
          employeeEmail={employee.email}
          employeeName={employee.name}
          open={showInviteDialog}
          onOpenChange={setShowInviteDialog}
        />
      )}
    </div>
  );
}

function DetailRow({
  icon: Icon,
  label,
  value,
  testId,
}: {
  icon: LucideIcon;
  label: string;
  value: string;
  testId?: string;
}) {
  return (
    <div className="flex items-center gap-3">
      <Icon className="h-4 w-4 text-muted-foreground" />
      <div className="flex-1">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-sm font-medium" data-testid={testId}>
          {value}
        </p>
      </div>
    </div>
  );
}

function InviteToPlatformDialog({
  orgId,
  employeeEmail,
  employeeName,
  open,
  onOpenChange,
}: {
  orgId: string;
  employeeEmail: string;
  employeeName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation('common');
  const { roles, isLoading: rolesLoading } = useRoles({ orgId });
  const inviteMutation = useInviteMember({ orgId });
  const [selectedRoleId, setSelectedRoleId] = useState('');

  const roleOptions = roles.map((r) => ({ value: r.id, label: r.name }));

  const handleInvite = async () => {
    if (!selectedRoleId) return;
    try {
      const result = await inviteMutation.mutateAsync({
        emailAddresses: [employeeEmail],
        roleId: selectedRoleId,
      });
      if (result.invitations.length > 0) {
        toast.success(i18n.t('settings:toast.platformInvitationSentTo', { employeeEmail: employeeEmail }));
        onOpenChange(false);
        setSelectedRoleId('');
      } else {
        const reason = result.failed[0]?.reason ?? 'Unknown error';
        toast.error(i18n.t('settings:toast.invitationFailed', { reason: reason }));
      }
    } catch {
      toast.error(i18n.t('settings:toast.failedToSendInvitation'));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[420px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5" />
            {t('employees.inviteToPlatform')}
          </DialogTitle>
          <DialogDescription>
            {t('employees.inviteToPlatformDescription', { name: employeeName, email: employeeEmail })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 py-2">
          <Label htmlFor="invite-role">{t('employees.role')}</Label>
          <NativeSelect
            id="invite-role"
            options={roleOptions}
            value={selectedRoleId}
            onChange={setSelectedRoleId}
            placeholder={t('employees.selectRole')}
            disabled={rolesLoading}
            data-testid="invite-role-select"
          />
          <p className="text-xs text-muted-foreground">
            {t('employees.chooseRole')}
          </p>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => { onOpenChange(false); }}>
            {t('employees.cancel')}
          </Button>
          <Button
            onClick={() => void handleInvite()}
            disabled={!selectedRoleId || inviteMutation.isPending}
            data-testid="send-invite-button"
          >
            {inviteMutation.isPending ? t('employees.sending') : t('employees.sendInvitation')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
